import axios from "axios";
import i18n from "../i18n";
import { financialLock } from "../helpers/financial-lock";
import { LOGIN_ROLES } from "../helpers/roles";
import { downscaleImageForAiEdit } from "../helpers/product-image-upload";

const rawBase = process.env.REACT_APP_API_URL || "";
const apiTargetUrl = rawBase.replace(/\/$/, "");
const useDevProxy = process.env.NODE_ENV === "development" && Boolean(apiTargetUrl);
const baseURL = useDevProxy ? "" : apiTargetUrl;
const CATALOG_REQUEST_TIMEOUT_MS = 60000;
const AUTH_TOKEN_KEY = "aa_shop_auth_token";
const AUTH_USER_KEY = "aa_shop_auth_user";
// Every role the API can issue a token to. This is an API-drift guard, not an
// authorization boundary: it exists so an unknown role string coming back from
// a newer API fails loudly at login instead of landing in a half-rendered
// shell. Adding a role means adding it in helpers/roles.js, not here.
const ALLOWED_LOGIN_ROLES = LOGIN_ROLES;

export const api = axios.create({
  baseURL: baseURL || undefined,
  headers: {
    Accept: "application/json",
  },
  timeout: 15000,
});

export function getApiBaseUrl() {
  return apiTargetUrl;
}

export function fetchApiTest() {
  return api.get("/api/test");
}

/** Public reference data: country, states[], cities_by_state{} */
export function fetchMexicoAddresses() {
  return api.get("/api/meta/mexico-addresses");
}

export async function resolveContactLead(params) {
  const response = await api.get("/api/leads/resolve", { params });
  return response.data;
}

export async function submitContactLead(payload) {
  const response = await api.post("/api/leads", payload);
  return response.data;
}

export function fetchAccountAddresses() {
  return api.get("/api/account/addresses");
}

export function createAccountAddress(payload) {
  return api.post("/api/account/addresses", payload);
}

export function fetchResellerMyProducts() {
  return api.get("/api/reseller/shop/my-products");
}

export function getStoredToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setStoredToken(token) {
  if (!token) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return;
  }
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function getStoredUser() {
  const raw = localStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (error) {
    localStorage.removeItem(AUTH_USER_KEY);
    return null;
  }
}

export function setStoredUser(user) {
  if (!user) {
    localStorage.removeItem(AUTH_USER_KEY);
    return;
  }
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
}

export function clearAuthStorage() {
  setStoredToken(null);
  setStoredUser(null);
}

function createApiError(message, responseData = {}) {
  const error = new Error(message);
  error.response = {
    data: {
      message,
      ...responseData
    }
  };
  return error;
}

function normalizeAuthResponse(data) {
  if (!data?.success || !data?.token || !data?.user) {
    throw createApiError(data?.message || "Login failed.");
  }

  if (data.user.is_active === false) {
    throw createApiError("Your account is inactive.");
  }

  if (!ALLOWED_LOGIN_ROLES.includes(data.user.role)) {
    throw createApiError("Your account role is not allowed to log in here.");
  }

  return {
    ...data,
    user: {
      ...data.user,
      role: data.user.role
    }
  };
}

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // The API answers messages and validation errors in the UI language.
  config.headers["Accept-Language"] = (i18n.resolvedLanguage || i18n.language || "es").slice(0, 2);
  return config;
});

// Financial sections answer 423 `reauth_required` until the user re-enters their
// password. A dialog (FinancialUnlockDialog) registers itself here; the failed
// request is held, retried once after a successful confirmation, and every
// request that hits the lock meanwhile waits on the same prompt.
let reauthHandler = null;
let pendingReauth = null;

export function setReauthHandler(handler) {
  reauthHandler = handler;
}

export async function confirmPassword(password) {
  const response = await api.post("/api/auth/confirm-password", { password });
  financialLock.markOpen(response.data?.data?.idle_minutes, { broadcast: true });
  return response.data;
}

/** Locks the financial sections now (idle screen, or the user pressed the lock button). */
/** Opens the password prompt on demand, for screens that show a "payment details locked" panel. */
export function promptFinancialUnlock() {
  return reauthHandler ? reauthHandler() : Promise.reject(new Error("no-prompt"));
}

export async function lockFinancialSections(reason = "manual") {
  try {
    await api.post("/api/auth/lock-financial", { reason });
  } catch {
    // The screen is covered either way; the server window also runs out on its own.
  }
}

export async function fetchFinancialStatus() {
  const response = await api.get("/api/auth/financial-status");
  return response.data;
}

export async function keepFinancialSectionsAlive() {
  try {
    await api.post("/api/auth/financial-keepalive");
  } catch {
    // Best effort.
  }
}

api.interceptors.response.use(
  (response) => {
    // Protected endpoints say how long the idle window is, which means a financial
    // section is open on screen.
    const minutes = response.headers?.["x-financial-idle-minutes"];
    if (minutes) financialLock.markOpen(minutes);
    return response;
  },
  async (error) => {
    const response = error?.response;
    const needsReauth = response?.status === 423 && response?.data?.code === "reauth_required";
    if (!needsReauth || !reauthHandler || error.config?._reauthRetried) {
      return Promise.reject(error);
    }

    if (!pendingReauth) {
      pendingReauth = reauthHandler().finally(() => {
        pendingReauth = null;
      });
    }
    try {
      await pendingReauth;
    } catch {
      return Promise.reject(error);
    }

    error.config._reauthRetried = true;
    return api.request(error.config);
  }
);

export async function login(payload) {
  const response = await api.post("/api/auth/login", payload);
  return normalizeAuthResponse(response.data);
}

export async function checkReferral(payload) {
  const response = await api.post("/api/auth/check-referral", payload);
  return response.data;
}

export async function register(payload) {
  const response = await api.post("/api/auth/register", payload);
  return normalizeAuthResponse(response.data);
}

/** Reseller admin: customer signup invite link (`/register?ref=...` — shop UI built separately). */
export async function getMyReferralLink() {
  const base =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : undefined;
  const response = await api.get("/api/reseller/me/referral-link", {
    params: base ? { base } : undefined
  });
  return response.data;
}

export async function fetchResellerProductVariants(resellerProductId) {
  const response = await api.get(`/api/reseller/products/${resellerProductId}/variants`);
  return response.data;
}

export async function updateResellerProductVariants(resellerProductId, payload) {
  const response = await api.put(`/api/reseller/products/${resellerProductId}/variants`, payload);
  return response.data;
}

export async function logout() {
  const response = await api.post("/api/auth/logout");
  return response.data;
}

export async function getCurrentUser() {
  const response = await api.get("/api/auth/me");
  return response.data;
}

export async function fetchShopCategories() {
  const response = await api.get("/api/shop/categories");
  return response.data;
}

export async function fetchShopProducts() {
  const response = await api.get("/api/shop/products", {
    timeout: CATALOG_REQUEST_TIMEOUT_MS,
  });
  return response.data;
}

export async function fetchShopProductLabels() {
  const response = await api.get("/api/shop/product-labels");
  return response.data;
}

export async function fetchCustomerProfile() {
  const response = await api.get("/api/customer/profile");
  return response.data;
}

export async function fetchCustomerAddress() {
  const response = await api.get("/api/customer/profile/address");
  return response.data;
}

export async function fetchCustomerOrders() {
  const response = await api.get("/api/customer/orders");
  return response.data;
}

export async function fetchCustomerOrder(orderId) {
  const response = await api.get(`/api/customer/orders/${orderId}`);
  return response.data;
}

/** Re-resolves a past customer order against the live store; writes nothing. */
export async function fetchCustomerOrderReorderPreview(orderId) {
  const response = await api.get(`/api/customer/shop/reorder/${orderId}/preview`);
  return response.data;
}

/**
 * Copies a past order into the customer's cart.
 * `payload.items` (quantities in items) lets the customer adjust before applying;
 * omit it to load every orderable line at its suggested quantity.
 */
export async function repeatCustomerOrder(orderId, payload = {}) {
  const response = await api.post(`/api/customer/shop/reorder/${orderId}`, payload);
  return response.data;
}

export async function fetchCustomerProducts() {
  const response = await api.get("/api/customer/shop/products", {
    timeout: CATALOG_REQUEST_TIMEOUT_MS,
  });
  return response.data;
}

export async function fetchResellerShopProducts() {
  const response = await api.get("/api/reseller/shop/products", {
    timeout: CATALOG_REQUEST_TIMEOUT_MS,
  });
  return response.data;
}

function getShopRolePrefix(role) {
  if (role === "reseller_admin") return "/api/reseller/shop";
  if (role === "user") return "/api/customer/shop";

  throw createApiError("You must be logged in to use the cart.");
}

export async function fetchShopCart(role) {
  const response = await api.get(`${getShopRolePrefix(role)}/cart`);
  return response.data;
}

export async function addShopCartItem(role, payload) {
  const response = await api.post(`${getShopRolePrefix(role)}/cart/items`, payload);
  return response.data;
}

export async function updateShopCartItem(role, cartItemId, payload) {
  const response = await api.patch(`${getShopRolePrefix(role)}/cart/items/${cartItemId}`, payload);
  return response.data;
}

export async function removeShopCartItem(role, cartItemId) {
  const response = await api.delete(`${getShopRolePrefix(role)}/cart/items/${cartItemId}`);
  return response.data;
}

export async function clearShopCart(role) {
  const response = await api.delete(`${getShopRolePrefix(role)}/cart`);
  return response.data;
}

export async function fetchShopCheckoutPreview(role) {
  const response = await api.post(`${getShopRolePrefix(role)}/checkout/preview`);
  return response.data;
}

export async function placeShopOrder(role, payload = {}) {
  const response = await api.post(`${getShopRolePrefix(role)}/checkout/place-order`, payload);
  return response.data;
}

export async function placeCustomerOrder(payload) {
  const response = await api.post("/api/customer/orders", payload);
  return response.data;
}

export async function updateCustomerProfile(payload) {
  const response = await api.patch("/api/customer/profile", payload);
  return response.data;
}

export async function updateCustomerAddress(payload) {
  const response = await api.patch("/api/customer/profile/address", payload);
  return response.data;
}

export async function deleteCustomerProfile() {
  const response = await api.delete("/api/customer/profile");
  return response.data;
}

export async function fetchAdminFinancialDashboard() {
  const response = await api.get("/api/admin/dashboard/financial");
  return response.data;
}

export async function fetchAdminDashboard() {
  const response = await api.get("/api/admin/dashboard");
  return response.data;
}

export async function fetchAdminUsers(params = {}) {
  const response = await api.get("/api/admin/users", { params });
  return response.data;
}

export async function fetchAdminResellerUsers(params = {}) {
  const response = await api.get("/api/admin/users", { params: { ...params, role: "reseller_admin" } });
  return response.data;
}

export async function fetchAdminUser(userId) {
  const response = await api.get(`/api/admin/users/${userId}`);
  return response.data;
}

export async function createAdminUser(payload) {
  const response = await api.post("/api/admin/users", payload);
  return response.data;
}

export async function updateAdminUser(userId, payload) {
  const response = await api.patch(`/api/admin/users/${userId}`, payload);
  return response.data;
}

export async function setAdminUserStatus(userId, isActive) {
  const response = await api.patch(`/api/admin/users/${userId}/status`, { is_active: isActive });
  return response.data;
}

export async function deleteAdminUser(userId) {
  const response = await api.delete(`/api/admin/users/${userId}`);
  return response.data;
}

export async function updateAdminUserRole(userId, payload) {
  const response = await api.patch(`/api/admin/users/${userId}/role`, payload);
  return response.data;
}

export async function fetchAdminRoles() {
  const response = await api.get("/api/admin/roles");
  return response.data;
}

export async function updateAdminRole(roleId, payload) {
  const response = await api.patch(`/api/admin/roles/${roleId}`, payload);
  return response.data;
}

export async function fetchAdminOrders(params = {}) {
  const response = await api.get("/api/admin/orders", { params });
  return response.data;
}

export async function fetchAdminOrder(orderId) {
  const response = await api.get(`/api/admin/orders/${orderId}`);
  return response.data;
}

export async function updateAdminOrder(orderId, payload) {
  if (payload instanceof FormData) {
    payload.append("_method", "PATCH");
    const response = await api.post(`/api/admin/orders/${orderId}`, payload);
    return response.data;
  }
  const response = await api.patch(`/api/admin/orders/${orderId}`, payload);
  return response.data;
}

export async function runAdminOrderApprovalAction(orderId, action, note = "") {
  const payload = { approval_action: action };
  if (note) {
    payload.payment_approval_note = note;
  }
  const response = await api.patch(`/api/admin/orders/${orderId}`, payload);
  return response.data;
}

// Carrier calls (Enviatodo) are slow; allow a longer timeout than the 15s default.
const SHIPPING_REQUEST_TIMEOUT_MS = 60000;

export async function getOrderShippingRates(orderId) {
  const response = await api.post(
    `/api/admin/orders/${orderId}/shipping/quote`,
    {},
    { timeout: SHIPPING_REQUEST_TIMEOUT_MS }
  );
  return response.data;
}

export async function generateOrderShippingLabel(orderId, body) {
  const response = await api.post(
    `/api/admin/orders/${orderId}/shipping/label`,
    body,
    { timeout: SHIPPING_REQUEST_TIMEOUT_MS }
  );
  return response.data;
}

export async function getOrderShippingLabel(orderId) {
  const response = await api.get(`/api/admin/orders/${orderId}/shipping/label`, {
    timeout: SHIPPING_REQUEST_TIMEOUT_MS
  });
  return response.data;
}

export async function trackOrderShipment(orderId) {
  const response = await api.get(`/api/admin/orders/${orderId}/shipping/track`);
  return response.data;
}

export async function fetchAdminProducts(params = {}) {
  const response = await api.get("/api/admin/products", { params });
  return response.data;
}

export async function fetchAdminProductPriceChangeLogs(params = {}) {
  const response = await api.get("/api/admin/product-price-change-logs", { params });
  return response.data;
}

const MULTIPART_UPLOAD_TIMEOUT_MS = 300000;

export async function createAdminProduct(payload) {
  const config = payload instanceof FormData ? { timeout: MULTIPART_UPLOAD_TIMEOUT_MS } : undefined;
  const response = await api.post("/api/admin/products", payload, config);
  return response.data;
}

export async function fetchAdminProduct(productId) {
  const response = await api.get(`/api/admin/products/${productId}`);
  return response.data;
}

export async function updateAdminProduct(productId, payload) {
  if (payload instanceof FormData) {
    payload.append("_method", "PATCH");
    const response = await api.post(`/api/admin/products/${productId}`, payload, {
      timeout: MULTIPART_UPLOAD_TIMEOUT_MS,
    });
    return response.data;
  }
  const response = await api.patch(`/api/admin/products/${productId}`, payload);
  return response.data;
}

// AI image generation regularly takes 1-2 minutes.
const AI_IMAGE_EDIT_TIMEOUT_MS = 300000;

export async function fetchAdminAiImageEditConfig() {
  const response = await api.get("/api/admin/ai-image-edit/config");
  return response.data;
}

export async function generateAdminProductImageAiPreview(productId, imageId, payload = {}) {
  const response = await api.post(
    `/api/admin/products/${productId}/gallery-images/${imageId}/ai-edit/preview`,
    payload,
    { timeout: AI_IMAGE_EDIT_TIMEOUT_MS }
  );
  return response.data;
}

export async function applyAdminProductImageAiEdit(productId, imageId, payload) {
  const response = await api.post(
    `/api/admin/products/${productId}/gallery-images/${imageId}/ai-edit/apply`,
    payload
  );
  return response.data;
}

export async function updateAdminProductGalleryImage(productId, imageId, payload) {
  const response = await api.patch(
    `/api/admin/products/${productId}/gallery-images/${imageId}`,
    payload
  );
  return response.data;
}

export async function reviewAdminProductVariantPrice(productId, variantId, action, note) {
  const response = await api.patch(
    `/api/admin/products/${productId}/variants/${variantId}/price-approval`,
    { action, note }
  );
  return response.data;
}

export async function generateAdminProductThumbnailAiPreview(productId, payload = {}) {
  const response = await api.post(
    `/api/admin/products/${productId}/thumbnail/ai-edit/preview`,
    payload,
    { timeout: AI_IMAGE_EDIT_TIMEOUT_MS }
  );
  return response.data;
}

export async function applyAdminProductThumbnailAiEdit(productId, payload) {
  const response = await api.post(
    `/api/admin/products/${productId}/thumbnail/ai-edit/apply`,
    payload
  );
  return response.data;
}

export async function generateAdminAiImagePreviewFromFile(file, prompt) {
  const upload = await downscaleImageForAiEdit(file);
  const payload = new FormData();
  payload.append("image", upload, upload.name);
  if (prompt) payload.append("prompt", prompt);
  const response = await api.post("/api/admin/ai-image-edit/preview-upload", payload, {
    timeout: AI_IMAGE_EDIT_TIMEOUT_MS,
  });
  return response.data;
}

export async function downloadAdminAiImagePreview(previewPath) {
  const response = await api.get("/api/admin/ai-image-edit/preview-file", {
    params: { preview_path: previewPath },
    responseType: "blob",
  });
  return response.data;
}

export async function discardAdminAiImagePreview(previewPath) {
  const response = await api.post("/api/admin/ai-image-edit/discard", { preview_path: previewPath });
  return response.data;
}

export async function fetchAdminProductCategories(params = {}) {
  const response = await api.get("/api/admin/product-categories", { params });
  return response.data;
}

export async function fetchAdminProductCategory(categoryId) {
  const response = await api.get(`/api/admin/product-categories/${categoryId}`);
  return response.data;
}

export async function createAdminProductCategory(payload) {
  const response = await api.post("/api/admin/product-categories", payload);
  return response.data;
}

export async function updateAdminProductCategory(categoryId, payload) {
  const response = await api.patch(`/api/admin/product-categories/${categoryId}`, payload);
  return response.data;
}

export async function deleteAdminProductCategory(categoryId) {
  const response = await api.delete(`/api/admin/product-categories/${categoryId}`);
  return response.data;
}

export async function fetchAdminMexicoStates() {
  const response = await api.get("/api/admin/mexico-locations/states");
  return response.data;
}

export async function fetchAdminMexicoCities(stateId, params = {}) {
  const response = await api.get(`/api/admin/mexico-locations/states/${stateId}/cities`, { params });
  return response.data;
}

export async function createAdminMexicoCity(payload) {
  const response = await api.post("/api/admin/mexico-locations/cities", payload);
  return response.data;
}

export async function updateAdminMexicoCity(cityId, payload) {
  const response = await api.patch(`/api/admin/mexico-locations/cities/${cityId}`, payload);
  return response.data;
}

export async function deleteAdminMexicoCity(cityId) {
  const response = await api.delete(`/api/admin/mexico-locations/cities/${cityId}`);
  return response.data;
}

export async function fetchAdminBankDatas(params = {}) {
  const response = await api.get("/api/admin/bank-datas", { params });
  return response.data;
}

export async function createAdminBankData(payload) {
  const response = await api.post("/api/admin/bank-datas", payload);
  return response.data;
}

export async function updateAdminBankData(bankDataId, payload) {
  const response = await api.patch(`/api/admin/bank-datas/${bankDataId}`, payload);
  return response.data;
}

export async function setAdminBankDataStatus(bankDataId, isActive) {
  const response = await api.patch(`/api/admin/bank-datas/${bankDataId}/status`, { is_active: isActive });
  return response.data;
}

export async function deleteAdminBankData(bankDataId) {
  const response = await api.delete(`/api/admin/bank-datas/${bankDataId}`);
  return response.data;
}

export async function fetchAdminResellerTerritories(params = {}) {
  const response = await api.get("/api/admin/reseller-territories", { params });
  return response.data;
}

export async function createAdminResellerTerritory(payload) {
  const response = await api.post("/api/admin/reseller-territories", payload);
  return response.data;
}

export async function setAdminResellerTerritoryPrimary(territoryId) {
  const response = await api.patch(`/api/admin/reseller-territories/${territoryId}/primary`);
  return response.data;
}

export async function deleteAdminResellerTerritory(territoryId) {
  const response = await api.delete(`/api/admin/reseller-territories/${territoryId}`);
  return response.data;
}

export async function fetchAdminGoogleLocations(params = {}) {
  const response = await api.get("/api/admin/google-locations", { params });
  return response.data;
}

export async function createAdminGoogleLocation(payload) {
  const response = await api.post("/api/admin/google-locations", payload);
  return response.data;
}

export async function updateAdminGoogleLocation(locationId, payload) {
  const response = await api.patch(`/api/admin/google-locations/${locationId}`, payload);
  return response.data;
}

export async function setAdminGoogleLocationStatus(locationId, isActive) {
  const response = await api.patch(`/api/admin/google-locations/${locationId}/status`, { is_active: isActive });
  return response.data;
}

export async function deleteAdminGoogleLocation(locationId) {
  const response = await api.delete(`/api/admin/google-locations/${locationId}`);
  return response.data;
}

export async function fetchAdminCatalogSettings() {
  const response = await api.get("/api/admin/catalog-settings");
  return response.data;
}

export async function updateAdminCatalogSettings(payload) {
  const response = await api.patch("/api/admin/catalog-settings", payload);
  return response.data;
}

// Each batch is a real round trip to the ERP's SQL Server, so this can take a
// while as the number of erp_reference_id-mapped variants grows.
const ERP_SYNC_TIMEOUT_MS = 120000;

export async function syncErpCatalog() {
  const response = await api.post(
    "/api/admin/erp/sync",
    {},
    { timeout: ERP_SYNC_TIMEOUT_MS }
  );
  return response.data;
}

export async function fetchAdminResellerDiscountTiers(params = {}) {
  const response = await api.get("/api/admin/reseller-discount-tiers", { params });
  return response.data;
}

export async function fetchAdminResellersWithoutDiscountTiers() {
  const response = await api.get("/api/admin/reseller-discount-tiers/resellers-without-tiers");
  return response.data;
}

export async function createAdminResellerDiscountTier(payload) {
  const response = await api.post("/api/admin/reseller-discount-tiers", payload);
  return response.data;
}

export async function updateAdminResellerDiscountTier(tierId, payload) {
  const response = await api.patch(`/api/admin/reseller-discount-tiers/${tierId}`, payload);
  return response.data;
}

export async function deleteAdminResellerDiscountTier(tierId) {
  const response = await api.delete(`/api/admin/reseller-discount-tiers/${tierId}`);
  return response.data;
}

export async function fetchAdminMainEmailConfig() {
  const response = await api.get("/api/admin/email-config/main");
  return response.data;
}

export async function updateAdminMainEmailConfig(payload) {
  const response = await api.patch("/api/admin/email-config/main", payload);
  return response.data;
}

export async function sendAdminEmailConfigTest(payload) {
  const response = await api.post("/api/admin/email-config/test", payload);
  return response.data;
}

export async function fetchAdminEmailConfigs(params = {}) {
  const response = await api.get("/api/admin/email-configs", { params });
  return response.data;
}

export async function fetchAdminEmailConfig(configId) {
  const response = await api.get(`/api/admin/email-configs/${configId}`);
  return response.data;
}

export async function createAdminEmailConfig(payload) {
  const response = await api.post("/api/admin/email-configs", payload);
  return response.data;
}

export async function updateAdminEmailConfig(configId, payload) {
  const response = await api.patch(`/api/admin/email-configs/${configId}`, payload);
  return response.data;
}

export async function setAdminEmailConfigStatus(configId, isActive) {
  const response = await api.patch(`/api/admin/email-configs/${configId}/status`, { is_active: isActive });
  return response.data;
}

export async function deleteAdminEmailConfig(configId) {
  const response = await api.delete(`/api/admin/email-configs/${configId}`);
  return response.data;
}

export async function fetchAdminMainWhatsAppConfig() {
  const response = await api.get("/api/admin/whatsapp-config/main");
  return response.data;
}

export async function updateAdminMainWhatsAppConfig(payload) {
  const response = await api.patch("/api/admin/whatsapp-config/main", payload);
  return response.data;
}

export async function sendAdminWhatsAppConfigTest(payload) {
  const response = await api.post("/api/admin/whatsapp-config/test", payload);
  return response.data;
}

export async function fetchAdminWhatsAppConfigs(params = {}) {
  const response = await api.get("/api/admin/whatsapp-configs", { params });
  return response.data;
}

export async function fetchAdminWhatsAppConfig(configId) {
  const response = await api.get(`/api/admin/whatsapp-configs/${configId}`);
  return response.data;
}

export async function createAdminWhatsAppConfig(payload) {
  const response = await api.post("/api/admin/whatsapp-configs", payload);
  return response.data;
}

export async function updateAdminWhatsAppConfig(configId, payload) {
  const response = await api.patch(`/api/admin/whatsapp-configs/${configId}`, payload);
  return response.data;
}

export async function setAdminWhatsAppConfigStatus(configId, isActive) {
  const response = await api.patch(`/api/admin/whatsapp-configs/${configId}/status`, { is_active: isActive });
  return response.data;
}

export async function deleteAdminWhatsAppConfig(configId) {
  const response = await api.delete(`/api/admin/whatsapp-configs/${configId}`);
  return response.data;
}

export async function fetchAdminLeads(params = {}) {
  const response = await api.get("/api/admin/leads", { params });
  return response.data;
}

export async function fetchAdminLead(leadId) {
  const response = await api.get(`/api/admin/leads/${leadId}`);
  return response.data;
}

export async function updateAdminLeadStatus(leadId, status) {
  const response = await api.patch(`/api/admin/leads/${leadId}/status`, { status });
  return response.data;
}

export async function fetchAdminInventory(params = {}) {
  const response = await api.get("/api/admin/inventory", { params });
  return response.data;
}

export async function fetchAdminInventoryResellers(params = {}) {
  const response = await api.get("/api/admin/inventory/resellers", { params });
  return response.data;
}

export async function createInventoryMovement(payload) {
  const response = await api.post("/api/admin/inventory/movements", payload);
  return response.data;
}

// Customers read the same feed through their own prefix; staff use /admin.
const notificationsBase = () =>
  getStoredUser()?.role === "user" ? "/api/customer/notifications" : "/api/admin/notifications";

export async function fetchAdminNotifications() {
  const response = await api.get(notificationsBase());
  return response.data;
}

export async function markAdminNotificationRead(id) {
  const response = await api.post(`${notificationsBase()}/${id}/read`);
  return response.data;
}

export async function markAllAdminNotificationsRead() {
  const response = await api.post(`${notificationsBase()}/read-all`);
  return response.data;
}

export async function fetchNotificationAnnouncements() {
  const response = await api.get("/api/admin/notifications/announcements");
  return response.data;
}

export async function retractNotificationAnnouncement(id) {
  const response = await api.delete(`/api/admin/notifications/announcements/${id}`);
  return response.data;
}

export async function sendNotificationAnnouncement(payload) {
  const response = await api.post("/api/admin/notifications/announcements", payload);
  return response.data;
}

export async function fetchResellerShopCategories() {
  const response = await api.get("/api/reseller/products/categories");
  return response.data;
}

export async function fetchCatalogPriceCategories() {
  const response = await api.get("/api/admin/products/price-increase/categories");
  return response.data;
}

export async function raiseCatalogPrices(payload) {
  const response = await api.post("/api/admin/products/price-increase", payload);
  return response.data;
}

export async function raiseResellerPrices(payload) {
  const response = await api.post("/api/reseller/products/price-increase", payload);
  return response.data;
}

export async function fetchResellerPurchaseOrders(params = {}) {
  const response = await api.get("/api/admin/reseller-purchase-orders", { params });
  return response.data;
}

export async function fetchResellerPurchaseOrder(orderId) {
  const response = await api.get(`/api/admin/reseller-purchase-orders/${orderId}`);
  return response.data;
}

export async function fetchResellerPurchaseOrderInvoice(orderId) {
  const response = await api.get(`/api/admin/reseller-purchase-orders/${orderId}/invoice`);
  return response.data;
}

/** Re-resolves a past purchase order against the live catalog; writes nothing. */
export async function fetchResellerPurchaseOrderReorderPreview(orderId) {
  const response = await api.get(`/api/admin/reseller-purchase-orders/${orderId}/reorder-preview`);
  return response.data;
}

export async function updateResellerPurchaseOrder(orderId, payload) {
  if (payload instanceof FormData) {
    payload.append("_method", "PATCH");
    const response = await api.post(`/api/admin/reseller-purchase-orders/${orderId}`, payload);
    return response.data;
  }
  const response = await api.patch(`/api/admin/reseller-purchase-orders/${orderId}`, payload);
  return response.data;
}

export async function fetchResellerDebits(userId, params = {}) {
  const response = await api.get(`/api/admin/resellers/${userId}/debits`, { params });
  return response.data;
}

export async function grantResellerDebit(userId, payload) {
  const response = await api.post(`/api/admin/resellers/${userId}/debits/grant`, payload);
  return response.data;
}

export async function adjustResellerDebit(userId, payload) {
  const response = await api.post(`/api/admin/resellers/${userId}/debits/adjust`, payload);
  return response.data;
}

export async function fetchResellerPurchaseOrderDebitLog(orderId) {
  const response = await api.get(`/api/admin/reseller-purchase-orders/${orderId}/debit-log`);
  return response.data;
}

export async function createResellerPurchaseOrder(payload) {
  const response = await api.post("/api/admin/reseller-purchase-orders", payload);
  return response.data;
}

export async function approveResellerPurchaseOrder(orderId) {
  const response = await api.patch(`/api/admin/reseller-purchase-orders/${orderId}/approve`);
  return response.data;
}

export async function rejectResellerPurchaseOrder(orderId) {
  const response = await api.patch(`/api/admin/reseller-purchase-orders/${orderId}/reject`);
  return response.data;
}

export async function fulfillResellerPurchaseOrder(orderId) {
  const response = await api.patch(`/api/admin/reseller-purchase-orders/${orderId}/fulfill`);
  return response.data;
}

export async function submitResellerPurchaseOrderPayment(orderId, payload) {
  const formData = payload instanceof FormData ? payload : new FormData();
  if (!(payload instanceof FormData)) {
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
  }
  const response = await api.post(`/api/admin/reseller-purchase-orders/${orderId}/payments`, formData);
  return response.data;
}

export async function reviewResellerPurchaseOrderPayment(orderId, paymentId, payload) {
  const response = await api.patch(
    `/api/admin/reseller-purchase-orders/${orderId}/payments/${paymentId}`,
    payload
  );
  return response.data;
}

export async function deleteResellerPurchaseOrderPayment(orderId, paymentId) {
  const response = await api.delete(`/api/admin/reseller-purchase-orders/${orderId}/payments/${paymentId}`);
  return response.data;
}

/**
 * Opens one of the private files behind /api/files/{type}/{id} -- payment
 * proofs, shipping labels, product documents.
 *
 * These used to be plain <a href> links, which worked only because that route
 * was signed-with-no-auth: a top-level navigation sends no Authorization
 * header. Now that the route requires a token (a signed URL leaks through logs,
 * history and Referer, so the string alone was the whole credential), the file
 * has to be fetched rather than navigated to.
 *
 * The absolute URL from the payload is reduced to a path so it goes back
 * through this axios instance: that attaches the bearer token, and in
 * development routes it via setupProxy instead of a cross-origin call to the
 * API port. Laravel recomputes the signature from the URL it receives, and both
 * paths reach it on the host that minted it, so the signature still validates.
 */
/**
 * Is this one of our own private-file links, as opposed to an external URL from
 * a carrier or provider? Only the former needs a bearer token.
 */
export function isPrivateFileUrl(candidate) {
  if (!candidate) return false;
  try {
    return new URL(candidate, window.location.origin).pathname.startsWith("/api/files/");
  } catch {
    return String(candidate).startsWith("/api/files/");
  }
}

export async function openPrivateFile(signedUrl) {
  if (!signedUrl) throw createApiError("No file to open.");

  let path = signedUrl;
  try {
    const parsed = new URL(signedUrl, window.location.origin);
    path = parsed.pathname + parsed.search;
  } catch {
    // Already relative -- use it as given.
  }

  const response = await api.get(path, { responseType: "blob" });
  const type = response.headers?.["content-type"] || "application/octet-stream";
  const objectUrl = window.URL.createObjectURL(new Blob([response.data], { type }));
  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");

  if (!opened) {
    // Pop-up blocked: fall back to a download so the click is not simply lost.
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  // Revoked on a delay: revoking immediately can race the new tab's own load.
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
}

export async function downloadAdminReport(type = "orders", params = {}) {
  const response = await api.get("/api/admin/reports/export", {
    params: { type, ...params },
    responseType: "blob"
  });
  return response;
}

export async function downloadAdminPriceListPdf(params = {}) {
  const response = await api.get("/api/admin/products/price-list/export.pdf", {
    params,
    responseType: "blob"
  });
  return response;
}

/**
 * Sends a SHA-256 hex hash to the server and returns duplicate-detection info.
 * Response shape: { duplicate: boolean, existing_file?: { original_filename, context_type, context_id, uploaded_at } }
 */
export function checkFileDuplicate(hash) {
  return api.post("/api/admin/files/check-duplicate", { hash });
}

export async function deleteDocument(documentId) {
  const response = await api.delete(`/api/admin/documents/${documentId}`);
  return response.data;
}

export function fetchResellerStorePaymentSettings() {
  return api.get("/api/reseller/store-settings/payment");
}

export function updateResellerStorePaymentSettings(payload) {
  return api.patch("/api/reseller/store-settings/payment", payload);
}

export async function fetchResellerEmailConfig() {
  const response = await api.get("/api/reseller/email-config");
  return response.data;
}

export async function updateResellerEmailConfig(payload) {
  const response = await api.patch("/api/reseller/email-config", payload);
  return response.data;
}

export async function sendResellerEmailConfigTest(payload) {
  const response = await api.post("/api/reseller/email-config/test", payload);
  return response.data;
}

export async function fetchResellerWhatsAppConfig() {
  const response = await api.get("/api/reseller/whatsapp-config");
  return response.data;
}

export async function updateResellerWhatsAppConfig(payload) {
  const response = await api.patch("/api/reseller/whatsapp-config", payload);
  return response.data;
}

export async function sendResellerWhatsAppConfigTest(payload) {
  const response = await api.post("/api/reseller/whatsapp-config/test", payload);
  return response.data;
}

export async function fetchCustomerPaymentInfo() {
  const response = await api.get("/api/customer/shop/payment-info");
  return response.data;
}
